import { Disposable } from 'using-statement';
import { AnyAction, IndigoStore } from 'store/store';
import { AnyAction as AnyReduxAction } from 'redux';
import IntervalBuffer from 'utils/IntervalBuffer';

export interface ActionsDispatcher extends Disposable {
    dispatch: (...actions: AnyReduxAction[]) => void;
}

/*
 * Actions dispatcher that defers dispatching to the store until "dispose" or "flush" is called
 * Implements disposable for use with "using"
 */
export class ActionsDeferredDispatcher
    implements ActionsDispatcher, Disposable
{
    private pendingActions: AnyReduxAction[] = [];

    public constructor(private store: IndigoStore) {}

    public dispatch(...actions: AnyReduxAction[]) {
        this.pendingActions.push(...actions);
    }

    public immediateDispatch(...actions: AnyReduxAction[]) {
        this.dispatch(...actions);
        this.flush();
    }

    public flush() {
        if (this.pendingActions.length > 0) {
            this.store.dispatch(this.pendingActions);

            this.pendingActions = [];
        }
    }

    public dispose() {
        this.flush();
    }
}

/*
 * Dispatches received actions immediately
 */
export class ActionsImmediateDispatcher implements ActionsDispatcher {
    public constructor(private store: IndigoStore) {}

    public dispatch(...actions: AnyReduxAction[]) {
        this.store.dispatch(actions);
    }
    public dispose() {
        // No action...
    }
}

export class ActionsDeferredDispatcherFactory {
    public store: IndigoStore | undefined = undefined;

    public new() {
        if (!this.store) {
            throw new Error('Store must be set');
        }

        return new ActionsDeferredDispatcher(this.store);
    }
}

export class ActionsIntervalDispatcher implements ActionsDispatcher {
    private intervalBuffer: IntervalBuffer<AnyAction>;

    public constructor(private store: IndigoStore) {
        this.intervalBuffer = new IntervalBuffer((data: AnyAction[]) => {
            store.dispatch(data);
        });
    }

    public dispatch(action: AnyAction): void {
        this.intervalBuffer.in([action]);
    }

    public dispose(): void {
        this.intervalBuffer.flush();
    }
}

export class ActionsIntervalDispatcherFactory {
    public store: IndigoStore | undefined = undefined;

    public new() {
        if (!this.store) {
            throw new Error('Store must be set');
        }

        return new ActionsIntervalDispatcher(this.store);
    }
}
